import { request } from '../request';

/**
 * 辅助函数：根据幂等键生成请求头
 */
function buildIdempotencyHeaders(idempotencyKey?: string): Record<string, string> | undefined {
  return idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined;
}

// ==========================================
// 前台/社区评论图片 API
// ==========================================

/**
 * 获取评论图片上传凭证
 * POST /comment-assets/presign
 *
 * @param data 图片预签名请求数据
 * @param idempotencyKey 幂等键
 */
export function presignCommentAsset(
  data: Api.Community.CommentAssetPresignRequest,
  idempotencyKey?: string
) {
  return request<Api.Community.CommentAssetPresignResponse>({
    url: '/comment-assets/presign',
    method: 'post',
    data,
    headers: buildIdempotencyHeaders(idempotencyKey)
  });
}

/**
 * 确认评论图片上传
 * POST /comment-assets/{asset_id}/complete
 *
 * @param assetId 图片资产 ID
 * @param idempotencyKey 幂等键
 */
export function completeCommentAsset(assetId: string, idempotencyKey?: string) {
  return request<Api.Community.CommentAssetCompleteResponse>({
    url: `/comment-assets/${assetId}/complete`,
    method: 'post',
    headers: buildIdempotencyHeaders(idempotencyKey)
  });
}

// ==========================================
// 前台/社区评论 API
// ==========================================

/**
 * 获取目标评论列表
 * GET /comments
 *
 * @param params 查询参数 (target_type, target_id, cursor, page_size)
 */
export function fetchComments(params: Api.Community.QueryCommentsParams) {
  return request<Api.Community.CommentPageResponse>({
    url: '/comments',
    method: 'get',
    params
  });
}

/**
 * 创建一级评论
 * POST /comments
 *
 * @param data 评论创建数据
 * @param idempotencyKey 幂等键
 */
export function createComment(
  data: Api.Community.CreateCommentRequest,
  idempotencyKey?: string
) {
  return request<Api.Community.CommentResponse>({
    url: '/comments',
    method: 'post',
    data,
    headers: buildIdempotencyHeaders(idempotencyKey)
  });
}

/**
 * 删除评论
 * DELETE /comments/{id}
 *
 * @param id 评论 ID
 * @param idempotencyKey 幂等键
 */
export function deleteComment(id: string, idempotencyKey?: string) {
  return request<Api.Community.ActionResponse>({
    url: `/comments/${id}`,
    method: 'delete',
    headers: buildIdempotencyHeaders(idempotencyKey)
  });
}

/**
 * 置顶评论
 * PUT /comments/{id}/pin
 *
 * @param id 评论 ID
 * @param idempotencyKey 幂等键
 */
export function pinComment(id: string, idempotencyKey?: string) {
  return request<Api.Community.PinResponse>({
    url: `/comments/${id}/pin`,
    method: 'put',
    headers: buildIdempotencyHeaders(idempotencyKey)
  });
}

/**
 * 取消评论置顶
 * DELETE /comments/{id}/pin
 *
 * @param id 评论 ID
 * @param idempotencyKey 幂等键
 */
export function unpinComment(id: string, idempotencyKey?: string) {
  return request<Api.Community.PinResponse>({
    url: `/comments/${id}/pin`,
    method: 'delete',
    headers: buildIdempotencyHeaders(idempotencyKey)
  });
}

/**
 * 获取评论回复列表
 * GET /comments/{id}/replies
 *
 * @param id 一级评论 ID
 * @param params 游标与分页参数
 */
export function fetchCommentReplies(
  id: string,
  params?: Api.Community.QueryCommentRepliesParams
) {
  return request<Api.Community.CommentPageResponse>({
    url: `/comments/${id}/replies`,
    method: 'get',
    params
  });
}

/**
 * 创建二级回复
 * POST /comments/{id}/replies
 *
 * @param id 一级评论 ID
 * @param data 回复数据
 * @param idempotencyKey 幂等键
 */
export function createCommentReply(
  id: string,
  data: Api.Community.ReplyCommentRequest,
  idempotencyKey?: string
) {
  return request<Api.Community.CommentResponse>({
    url: `/comments/${id}/replies`,
    method: 'post',
    data,
    headers: buildIdempotencyHeaders(idempotencyKey)
  });
}

/**
 * 举报评论
 * POST /comments/{id}/reports
 *
 * @param id 评论 ID
 * @param data 举报数据 (reason_code, description)
 * @param idempotencyKey 幂等键
 */
export function reportComment(
  id: string,
  data: Api.Community.CommentReportRequest,
  idempotencyKey?: string
) {
  return request<Api.Community.ActionResponse>({
    url: `/comments/${id}/reports`,
    method: 'post',
    data,
    headers: buildIdempotencyHeaders(idempotencyKey)
  });
}

/**
 * 获取我的评论
 * GET /me/comments
 *
 * @param params 分页参数 (page, page_size)
 */
export function fetchMyComments(params?: Api.Community.QueryMyCommentsParams) {
  return request<Api.Community.CommentListResponse>({
    url: '/me/comments',
    method: 'get',
    params
  });
}

// ==========================================
// 前台/社区话题 API
// ==========================================

/**
 * 查询启用的社区话题
 * GET /community/topics
 */
export function fetchCommunityTopics() {
  return request<Api.Community.TopicListResponse>({
    url: '/community/topics',
    method: 'get'
  });
}

/**
 * 查询热门社区话题
 * GET /community/topics/hot
 *
 * @param params limit: 返回数量，默认 10，最大 100
 */
export function fetchHotCommunityTopics(params?: { limit?: number }) {
  return request<Api.Community.HotTopicListResponse>({
    url: '/community/topics/hot',
    method: 'get',
    params
  });
}


// ==========================================
// 前台/社区动态 API
// ==========================================

/**
 * 分页获取社区广场动态
 * GET /community/posts
 *
 * @param params 筛选与分页参数 (topic_ids 话题ID列表, sort 排序[latest/hot], page 页码, page_size 每页数量[1-100])
 */
export function fetchCommunityPosts(params?: Api.Community.QueryPostsParams) {
  const formattedParams = {
    ...params,
    topic_ids: Array.isArray(params?.topic_ids) ? params.topic_ids.join(',') : params?.topic_ids
  };

  return request<Api.Community.PostListResponse>({
    url: '/community/posts',
    method: 'get',
    params: formattedParams
  });
}

/**
 * 创建社区动态草稿或提交机器审核
 * POST /community/posts
 *
 * @param data 动态创建数据 (title 标题, content 内容, topic_ids 话题标识, media_assets 媒体资产, submit 是否提交审核)
 * @param idempotencyKey 请求头 Idempotency-Key 幂等键；重复提交将返回首次创建的动态
 */
export function createCommunityPost(
  data: Api.Community.PostRequest,
  idempotencyKey?: string
) {
  return request<Api.Community.PostResponse>({
    url: '/community/posts',
    method: 'post',
    data,
    headers: buildIdempotencyHeaders(idempotencyKey)
  });
}

/**
 * 获取已发布社区动态详情
 * GET /community/posts/{id}
 *
 * @param id 动态 ID
 */
export function fetchCommunityPostDetail(id: string) {
  return request<Api.Community.PostResponse>({
    url: `/community/posts/${id}`,
    method: 'get'
  });
}

/**
 * 编辑我的社区动态
 * PUT /community/posts/{id}
 *
 * @param id 动态 ID
 * @param data 动态编辑数据 (title 标题, content 内容, topic_ids 话题标识, media_assets 媒体资产, submit 是否提交审核)
 * @param idempotencyKey 请求头 Idempotency-Key 幂等键；重复提交返回首次编辑后的动态
 */
export function updateCommunityPost(
  id: string,
  data: Api.Community.PostRequest,
  idempotencyKey?: string
) {
  return request<Api.Community.PostResponse>({
    url: `/community/posts/${id}`,
    method: 'put',
    data,
    headers: buildIdempotencyHeaders(idempotencyKey)
  });
}

/**
 * 删除我的社区动态
 * DELETE /community/posts/{id}
 *
 * @param id 动态 ID
 */
export function deleteCommunityPost(id: string) {
  return request<Record<string, boolean>>({
    url: `/community/posts/${id}`,
    method: 'delete'
  });
}

/**
 * 搜索已发布的社区动态
 * GET /community/posts/search
 *
 * @param params 搜索与游标参数 (keyword 搜索关键词[留空时返回已发布动态], sort 排序[latest/hot], before 游标时间[RFC3339], before_id 动态ID游标, before_score 热门排序游标分数, page_size 每页数量[1-100])
 */
export function searchCommunityPosts(params?: Api.Community.QuerySearchPostsParams) {
  return request<Api.Community.PostResponse[]>({
    url: '/community/posts/search',
    method: 'get',
    params
  });
}

/**
 * 获取我发布的社区动态
 * GET /community/me/posts
 *
 * @param params 游标与分页参数 (before 创建时间游标[RFC3339], before_id 同创建时间动态ID游标, page_size 每页数量[1-100])
 */
export function fetchMyCommunityPosts(params?: Api.Community.QueryMyPostsParams) {
  return request<Api.Community.PostResponse[]>({
    url: '/community/me/posts',
    method: 'get',
    params
  });
}

/**
 * 获取社区动态视频的 HLS 播放清单
 * GET /community/posts/{id}/media/{asset_id}/manifest
 *
 * @param id 动态 ID
 * @param assetId 视频上传资产 ID
 */
export function fetchCommunityPostVideoManifest(id: string, assetId: string) {
  return request<string>({
    url: `/community/posts/${id}/media/${assetId}/manifest`,
    method: 'get'
  });
}

/**
 * 重试失败的社区视频转码
 * POST /community/posts/{id}/media/{asset_id}/retry
 *
 * @param id 动态 ID
 * @param assetId 视频上传资产 ID
 * @param idempotencyKey 请求头 Idempotency-Key 幂等键；重复重试返回首次结果
 */
export function retryCommunityPostVideoTranscode(
  id: string,
  assetId: string,
  idempotencyKey?: string
) {
  return request<Api.Community.PostResponse>({
    url: `/community/posts/${id}/media/${assetId}/retry`,
    method: 'post',
    headers: buildIdempotencyHeaders(idempotencyKey)
  });
}

// ==========================================
// 前台/社区互动 API
// ==========================================

/**
 * 点赞已发布的社区动态
 * PUT /community/posts/{id}/like
 *
 * @param id 动态 ID
 */
export function likeCommunityPost(id: string) {
  return request<Api.Community.InteractionResponse>({
    url: `/community/posts/${id}/like`,
    method: 'put'
  });
}

/**
 * 取消点赞社区动态
 * DELETE /community/posts/{id}/like
 *
 * @param id 动态 ID
 */
export function unlikeCommunityPost(id: string) {
  return request<Api.Community.InteractionResponse>({
    url: `/community/posts/${id}/like`,
    method: 'delete'
  });
}

/**
 * 记录一次有效的社区动态分享
 * POST /community/posts/{id}/shares
 *
 * @param id 动态 ID
 * @param data 分享渠道数据 (share_channel)
 */
export function shareCommunityPost(id: string, data: Api.Community.ShareRequest) {
  return request<Api.Community.InteractionResponse>({
    url: `/community/posts/${id}/shares`,
    method: 'post',
    data
  });
}

// ==========================================
// 前台/社区举报 API
// ==========================================

/**
 * 举报已发布的社区动态
 * POST /community/posts/{id}/reports
 *
 * @param id 动态 ID
 * @param data 举报数据 (reason_code, description)
 */
export function reportCommunityPost(
  id: string,
  data: Api.Community.PostReportRequest
) {
  return request<Record<string, boolean>>({
    url: `/community/posts/${id}/reports`,
    method: 'post',
    data
  });
}
